import React, { Fragment, useEffect, useMemo, useState } from "react";
import { Col, Row } from "reactstrap";
import PropTypes from "prop-types";
import {
  CircularProgress,
  Collapse,
  DialogContentText,
  IconButton,
  Stack,
} from "@mui/material";
import { ExpandLess, ExpandMore } from "@mui/icons-material";

import Backdrop from "Components/Common/Backdrop";
import AuthorizedPersonDetailsView from "Components/Common/ViewApplicationDetailComponents/AuthorizedPersonDetailsView";
import ConnectionLoadView from "Components/Common/ViewApplicationDetailComponents/ConnectionLoadView";
import DeclarationView from "Components/Common/ViewApplicationDetailComponents/DeclarationView";
import UtilityAddressView from "Components/Common/ViewApplicationDetailComponents/UtilityAddressView";
import SolarDetailsView from "Components/Common/ViewApplicationDetailComponents/SolarDetailsView";
import DocumentsView from "Components/Common/ViewApplicationDetailComponents/DocumentsView";
import AccessoriesListView from "Components/Common/ViewApplicationDetailComponents/AccessoriesListView";
import ApplicationInfoView from "Components/Common/ViewApplicationDetailComponents/ApplicationInfoView";
import DcsDemandView from "Components/Common/ViewApplicationDetailComponents/DcsDemandView";
import MeterRequisitionView from "Components/Common/ViewApplicationDetailComponents/MeterRequisitionView";
import SewageDemandView from "Components/Common/ViewApplicationDetailComponents/SewageDemandView";
import WaterDemandView from "Components/Common/ViewApplicationDetailComponents/WaterDemandView";
import OtherApplicationExtraFieldsView from "Components/Common/ViewApplicationDetailComponents/OtherApplicationExtraFieldsView.js";
import PersonalDetailsView from "Components/Common/ViewApplicationDetailComponents/PersonalDetailsView";
import Modal from "Components/Common/Modal";
import { getOptionValue } from "common/enums";

import {
  useLazyApplicationDetailQuery,
  useLazyGetPowerUtilityApplicationsQuery,
} from "apis/ApplicationRequestDetail";
import {
  useLazyGetOtherApplicationDetailQuery,
  useLazyGetOtherUtilityApplicationsQuery,
} from "apis/otherApplicationDetails";

const ViewConsumerApplications = ({ consumerId, handleClose, utilityId }) => {
  const [
    getPowerUtilityApplications,
    {
      data: { data: { items: powerUtilityApplications } = {} } = {},
      isLoading: isPowerApplicationsLoading,
    },
  ] = useLazyGetPowerUtilityApplicationsQuery({
    utilityConsumerId: consumerId,
  });

  const [
    getOtherUtilityApplications,
    {
      data: { data: { items: otherUtilityApplications } = {} } = {},
      isLoading: isOtherApplicationsLoading,
    },
  ] = useLazyGetOtherUtilityApplicationsQuery({
    utilityConsumerId: consumerId,
  });

  const [
    getPowerApplicationDetailByApplicationId,
    {
      data: { data: powerApplicationsData } = {},
      isLoading: isPowerApplicationDataLoading,
      isFetching: isPowerApplicationDataFetching,
    },
  ] = useLazyApplicationDetailQuery();

  const [
    getOtherApplicationDetailByApplicationId,
    {
      data: { data: otherApplicationsData } = {},
      isLoading: isOtherApplicationDataLoading,
      isFetching: isOtherApplicationDataFetching,
    },
  ] = useLazyGetOtherApplicationDetailQuery();

  const [applicationId, setApplicationId] = useState();

  const applications = useMemo(
    () => powerUtilityApplications ?? otherUtilityApplications,
    [otherUtilityApplications, powerUtilityApplications]
  );

  const applicationData = useMemo(
    () => powerApplicationsData ?? otherApplicationsData,
    [powerApplicationsData, otherApplicationsData]
  );
  const handleToggle = (id) => {
    if (applicationId === id) {
      setApplicationId("");
    } else {
      setApplicationId(id);
    }
  };

  useEffect(() => {
    if (utilityId === getOptionValue("utilityOptions", "Power")) {
      getPowerUtilityApplications({ utilityConsumerId: consumerId });
    } else {
      getOtherUtilityApplications({ utilityConsumerId: consumerId });
    }
  }, [consumerId, utilityId]);

  useEffect(() => {
    if (applicationId) {
      (async () => {
        try {
          if (utilityId === getOptionValue("utilityOptions", "Power")) {
            await getPowerApplicationDetailByApplicationId(applicationId);
          } else {
            await getOtherApplicationDetailByApplicationId({
              applicationId,
            });
          }
        } catch (error) {
          console.error(error);
        }
      })();
    }
  }, [applicationId]);

  useEffect(() => {
    if (applications?.length === 1) {
      setApplicationId(applications[0].id);
    }
  }, [applications]);

  if (isPowerApplicationsLoading || isOtherApplicationsLoading) {
    return <Backdrop open />;
  }

  return (
    <Modal
      isOpen
      handleClose={handleClose}
      title="Utility Consumer Applications"
      maxWidth="lg"
    >
      {applications?.map(
        ({
          id,
          applicationNumber,
          applicationType: { name: applicationName, id: applicationTypeId },
          applicationStatus: { name: statusName },
        }) => (
          <Fragment key={id}>
            <div
              className="fs-4 fw-semibold mb-3 border-bottom"
              onClick={() => handleToggle(id)}
              key={id}
            >
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                className="cursor-pointer"
              >
                <div className="d-flex align-items-center gap-1">
                  <p className="fw-bold mb-0">{`${applicationName} (${applicationNumber})`}</p>
                  <p className="fw-light fs-5 mb-0">{`(${statusName})`}</p>
                </div>
                <IconButton>
                  {applicationId === id ? <ExpandLess /> : <ExpandMore />}
                </IconButton>
              </Stack>
            </div>
            <DialogContentText variant="subtitle2" component="div">
              <Collapse in={applicationId === id} timeout="auto" unmountOnExit>
                {isPowerApplicationDataLoading ||
                isPowerApplicationDataFetching ||
                isOtherApplicationDataLoading ||
                isOtherApplicationDataFetching ? (
                  <div className="d-flex justify-content-center">
                    <CircularProgress size={30} />
                  </div>
                ) : (
                  <>
                    {applicationData && (
                      <Row className="d-flex mb-2 justify-content-center">
                        <Col lg={6} md={10} className="mb-1">
                          <ApplicationInfoView
                            applicationInfo={{
                              imageUrl:
                                applicationData.authorizedPerson?.imageUrl ??
                                "",
                              applicationNumber:
                                applicationData.applicationNumber,
                              consumerNumber: applicationData.consumerNumber,
                              consumerName:
                                applicationData.applicant.applicantName,
                              applicationTypeName:
                                applicationData.applicationType.name,
                              submissionDate: applicationData.submissionDate,
                              disconnectionDate:
                                applicationData.disconnectionDate,
                              demandEstimateUrl:
                                applicationData.demandEstimateUrl,
                              demandEstimateReceiptUrl:
                                applicationData.demandEstimateReceiptUrl,
                              utilities: applicationData.utilities,
                            }}
                          />
                        </Col>
                      </Row>
                    )}

                    {!!applicationData?.applicant && (
                      <PersonalDetailsView
                        applicant={applicationData.applicant}
                        isExpandable={false}
                      />
                    )}

                    {!!applicationData?.authorizedPerson && (
                      <AuthorizedPersonDetailsView
                        authorizedPerson={applicationData.authorizedPerson}
                        isExpandable={false}
                      />
                    )}

                    {!!applicationData?.connectionLoad && (
                      <ConnectionLoadView
                        applicationData={{
                          applicationId,
                          applicationType: applicationData.applicationType,
                          applicationStatus: applicationData.applicationStatus,
                          connectionLoad: applicationData.connectionLoad,
                          tariff: applicationData.tariff,
                          utilityConsumerId: applicationData.utilityConsumerId,
                          isSolar: applicationData.isSolar,
                          isGreenPower:
                            !!applicationData.connectionLoad.greenPower,
                        }}
                        isExpandable={false}
                      />
                    )}

                    {(applicationData?.totalBuildupArea ||
                      applicationData?.totalNumberOfOccupants ||
                      applicationData?.expectedConnectionStartDate ||
                      applicationData?.expectedConnectionEndDate) && (
                      <OtherApplicationExtraFieldsView
                        extraValues={{
                          tariff: applicationData.tariff,
                          applicationType: applicationData.applicationType,
                          totalBuildupArea: applicationData.totalBuildupArea,
                          totalNumberOfOccupants:
                            applicationData.totalNumberOfOccupants,
                          expectedConnectionStartDate:
                            applicationData.expectedConnectionStartDate,
                          expectedConnectionEndDate:
                            applicationData.expectedConnectionEndDate,
                          totalBuildupAreaUnit:
                            applicationData.totalBuildupAreaUnit,
                          totalNumberOfOccupantsUnit:
                            applicationData.totalNumberOfOccupantsUnit,
                        }}
                      />
                    )}

                    {applicationData?.dcsDemand && (
                      <>
                        <DcsDemandView
                          dcsDemand={applicationData.dcsDemand}
                          applicationTypeId={applicationTypeId}
                        />
                        <MeterRequisitionView
                          dcsDemand={applicationData.dcsDemand}
                        />
                      </>
                    )}

                    {applicationData?.sewageDemand && (
                      <SewageDemandView
                        sewageDemand={applicationData.sewageDemand}
                        applicationTypeId={applicationTypeId}
                      />
                    )}

                    {applicationData?.waterDemand && (
                      <WaterDemandView
                        waterDemand={applicationData.waterDemand}
                        applicationTypeId={applicationTypeId}
                      />
                    )}

                    {!!applicationData?.utilityAddress && (
                      <UtilityAddressView
                        utilityAddress={applicationData.utilityAddress}
                        utilities={applicationData.utilities}
                        isExpandable={false}
                      />
                    )}

                    {!!applicationData?.solar && (
                      <SolarDetailsView
                        solar={applicationData.solar}
                        isExpandable={false}
                        applicationId={applicationData.id}
                      />
                    )}

                    {!!applicationData?.declaration && (
                      <DeclarationView
                        declaration={applicationData.declaration}
                        isExpandable={false}
                      />
                    )}

                    {!!applicationData?.documents?.length && (
                      <DocumentsView
                        documents={applicationData.documents}
                        isExpandable={false}
                      />
                    )}

                    {!!applicationData?.accessories?.length && (
                      <AccessoriesListView
                        accessories={applicationData.accessories}
                        isExpandable={false}
                      />
                    )}
                  </>
                )}
              </Collapse>
            </DialogContentText>
          </Fragment>
        )
      )}
    </Modal>
  );
};

ViewConsumerApplications.propTypes = {
  consumerId: PropTypes.string,
  handleClose: PropTypes.func,
  utilityId: PropTypes.number,
};

export default ViewConsumerApplications;
